import StateMachine from 'javascript-state-machine';
import _ from 'lodash';
import moment from 'moment-timezone';
import { DEBOUNCE } from '@/core/core.constants';
import { sqDurationStore, sqTrendStore, sqWorkbenchStore, sqWorkbookStore, sqWorksheetStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { parseISODate } from '@/datetime/dateTime.utilities';
import { count as pendingRequestCount } from '@/requests/pendingRequests.utilities';
import { handleInvestigateRangeUpdate } from '@/scatterPlot/scatterPlot.actions';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import {
  getDurationDisplayRange,
  getDurationInvestigateRange,
  withAutoUpdateIntervals,
} from '@/trendData/duration.utilities';
import { fetchAllItems, fetchTableAndChartCapsules } from '@/trendData/trend.actions';
import { AUTO_UPDATE, DEFAULT_DISPLAY_RANGE_DURATION_DAYS } from '@/trendData/trendData.constants';
import { successToast, warnToast } from '@/utilities/toast.utilities';
import { setLockedChangeMade } from '@/workbook/workbook.actions';
import { WORKSHEET_VIEW } from '@/worksheet/worksheet.constants';
import { getWorkstep, pushWorkstep, setCurrentWorkstep } from '@/worksteps/worksteps.utilities';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { AUTO_UPDATE_MODES } from '@/trendData/trendData.constants';

export const ENTRY_STATE_MACHINE_TIMEOUT = 30 * 1000;

type RangeType = 'displayRange' | 'investigateRange';

// Custom FSM instance with event methods dynamically added by the library
interface DurationFSM extends InstanceType<typeof StateMachine> {
  setDuration: (duration: moment.Duration | number | moment.Moment) => void;
  setStart: (start: moment.Moment | undefined) => void;
  setEnd: (end: moment.Moment | undefined) => void;
  reset: () => void;
}

type FSMTimeouts = {
  displayRange: number | undefined;
  investigateRange: number | undefined;
  timerLength: number;
};

type FSMStructure = {
  displayRange: DurationFSM;
  investigateRange: DurationFSM;
  timeouts: FSMTimeouts;
};

let debouncedOnDurationChange: ReturnType<typeof _.debounce<() => unknown>> | undefined;
let debouncedOnInvestigateChange: ReturnType<typeof _.debounce<() => void>> | undefined;
let isAutoUpdate: boolean | undefined;
let autoUpdateHeartbeat: number | undefined;
let isAutoUpdateInit: boolean | undefined;
let autoUpdatePauses = 0;

/**
 * FSM (finite state machine) to "remember" what individual date/duration fields the user has last modified, in
 * order to make an informed decision about which of the other two fields to modify. For example, if the user
 * enters a new start date then changes the duration, they probably expect that the end date is adjusted.
 * The FSM is not used if both start and end fields are modified simultaneously (e.g. step forward/back,
 * drag region, etc.) Display Range and Investigate Range have separate FSMs but the same behavior.
 * When no fields are individually modified in 30 seconds, the state machine resets to its default state.
 */
const FSM: FSMStructure = {
  displayRange: createFSM('displayRange'),
  investigateRange: createFSM('investigateRange'),
  timeouts: {
    displayRange: undefined,
    investigateRange: undefined,
    timerLength: ENTRY_STATE_MACHINE_TIMEOUT, // 30 second timeout to reset state machine
  },
};

export const autoUpdate = {
  initialize: autoUpdateInitialize,
  setMode: autoUpdateSetMode,
  setManualInterval: autoUpdateSetManualInterval,
  allowed: autoUpdateAllowed,
  pause: autoUpdatePause,
};
export const displayRange = {
  updateDuration: (duration: moment.Duration | number | moment.Moment) =>
    triggerUpdateDuration(duration as moment.Duration, 'displayRange'),
  updateStart: (start: moment.Moment | undefined) => triggerUpdateStart(start as moment.Moment, 'displayRange'),
  updateEnd: (end: moment.Moment | undefined) => triggerUpdateEnd(end as moment.Moment, 'displayRange'),
  updateTimes: displayRangeUpdateTimes,
  resetTimes: displayRangeResetTimes,
  copyFromInvestigateRange: displayRangeCopyFromInvestigateRange,
  stepBackHalf: _.partial(displayRangeStepByDuration, -0.5),
  stepBack: _.partial(displayRangeStepByDuration, -1.0),
  stepForward: _.partial(displayRangeStepByDuration, 1.0),
  stepForwardHalf: _.partial(displayRangeStepByDuration, 0.5),
  stepToEnd: displayRangeStepToEnd,
  copyToAllWorksheets: displayRangeCopyToAllWorksheets,
  setParamsInStore: displayRangeSetParamsInStore,
};
export const investigateRange = {
  updateDuration: (duration: moment.Duration | number | moment.Moment) =>
    triggerUpdateDuration(duration as moment.Duration, 'investigateRange'),
  updateStart: (start: moment.Moment | undefined) => triggerUpdateStart(start as moment.Moment, 'investigateRange'),
  updateEnd: (end: moment.Moment | undefined) => triggerUpdateEnd(end as moment.Moment, 'investigateRange'),
  updateTimes: investigateRangeUpdateTimes,
  copyFromDisplayRange: investigateRangeCopyFromDisplayRange,
};

const exposedExports = {
  investigateRange,
  displayRange,
  autoUpdate,
};

/************************************************ Auto Update ****************************************************/

function getAutoUpdateInterval() {
  const { autoUpdate, displayRange } = sqDurationStore.getState();
  return withAutoUpdateIntervals(autoUpdate, displayRange.end - displayRange.start).interval;
}

/**
 * Initializes auto update and starts the auto update heartbeat interval timer.
 */
export function autoUpdateInitialize() {
  let lastInterval: number | undefined;
  let next = moment().utc().valueOf() + getAutoUpdateInterval();

  autoUpdate(true);
  window.clearTimeout(autoUpdateHeartbeat);
  autoUpdateHeartbeat = window.setInterval(_.ary(autoUpdate, 0), 1000, false);

  /**
   * Auto update worker function that is called duration initialization and by the $interval timer.
   *
   * @param isInit - If true and auto update is allowed and enabled then now is immediately updated
   * If falsy, then now is updated if auto update is allowed, enabled, and an update is required.
   */
  function autoUpdate(isInit: boolean) {
    const isCapsuleTime = sqTrendStore.isTrendViewCapsuleTime();

    const now = moment().utc().valueOf();
    const interval = getAutoUpdateInterval();

    const intervalChanged = interval !== lastInterval;
    lastInterval = interval;

    // If the interval was changed, then use it to compute the next update time. Otherwise,
    // delay the start of next update interval until after all async operations have completed.
    if (intervalChanged || pendingRequestCount() !== 0) {
      next = now + interval;
    }

    const updateNeeded = now >= next;

    if (
      autoUpdatePauses === 0 &&
      autoUpdateAllowed() &&
      sqDurationStore.getState().autoUpdate.mode !== AUTO_UPDATE.MODES.OFF &&
      sqDurationStore.getState().autoUpdate.mode !==
        AUTO_UPDATE.MODES.OVERRIDE_VIA_DISPLAY_RANGE_QUERY_PARAMS_TO_SHOW_NOW_CURSOR &&
      (isInit || updateNeeded)
    ) {
      try {
        isAutoUpdate = true;
        isAutoUpdateInit = isInit;
        sqDurationStore.actions.setNow(now);
        if (!isCapsuleTime) {
          displayRange.updateEnd(moment.utc(now + sqDurationStore.getState().autoUpdate.offset));
        } else {
          displayRange.updateTimes(
            moment.utc(getDurationDisplayRange().start),
            moment.utc(now + sqDurationStore.getState().autoUpdate.offset),
            true,
          );
        }

        next = now + interval;
      } finally {
        isAutoUpdate = false;
        isAutoUpdateInit = false;
      }
    }
  }
}

/**
 * Determines if auto update is allowed. It is not allowed in some worksheet modes, such as capsule time and
 * scatter plot, because the charts are not set up to handle it. It is also not allowed when capturing a screenshot
 * since that system needs to use the exact time range in the store, not a calculated one, so that httpCache works.
 *
 * @returns {Boolean} - true if allowed, false otherwise
 */
export function autoUpdateAllowed() {
  const allowedViews = [WORKSHEET_VIEW.TREEMAP, WORKSHEET_VIEW.TREND, WORKSHEET_VIEW.TABLE];
  return (!headlessRenderMode() && _.includes(allowedViews, sqWorksheetStore.view.key)) || sqWorkbookStore.isVantage;
}

/**
 * Holds the display range still until the returned function is called, leaving the saved auto update mode as it is.
 * Pauses nest, so auto update resumes once every holder has let go, and catches up on its next heartbeat.
 */
export function autoUpdatePause(): () => void {
  let held = true;
  autoUpdatePauses += 1;

  return () => {
    if (held) {
      held = false;
      autoUpdatePauses -= 1;
    }
  };
}

/**
 * Sets the auto update mode
 *
 * @param {String} mode - one of AUTO_UPDATE.MODES (either AUTO, MANUAL or OFF)
 */
export function autoUpdateSetMode(mode: keyof typeof AUTO_UPDATE_MODES) {
  sqDurationStore.actions.setMode(mode);
}

/**
 * Commands the duration store to record the current auto update offset (i.e. the number of milliseconds between
 * now and the display range end).
 */
export function autoUpdateComputeOffset() {
  sqDurationStore.actions.computeOffset();
}

/**
 * Sets the manual update interval
 *
 * @param manualInterval - object container for manual interval properties
 * @param manualInterval.value - numeric value of interval
 * @param manualInterval.units - string value of units
 */
export function autoUpdateSetManualInterval(manualInterval: ValueWithUnitsItem) {
  sqDurationStore.actions.setManualInterval(manualInterval);
}

/************************************************ Finite State Machine *******************************************/

function getRange(rangeType: RangeType) {
  return rangeType === 'displayRange' ? getDurationDisplayRange() : getDurationInvestigateRange();
}

/**
 * Create a finite state machine for either the display or investigate range. The FSM determines which field to
 * modify when only one of the three fields - start, duration, end - is changed.
 *
 * @param rangeType - Range to control, 'displayRange' or 'investigateRange'
 * @return StateMachine FSM object
 */
export function createFSM(rangeType: RangeType): DurationFSM {
  const config = {
    init: 'none',
    transitions: generateEvents(),
    methods: generateCallbacks(rangeType),
  };
  return new StateMachine(config) as DurationFSM;
}

/**
 * Create FSM callbacks for the specified range, either display or investigate.
 *
 * @param rangeType - Range to control, 'displayRange' or 'investigateRange'
 * @return Object containing all FSM state callbacks
 */
export function generateCallbacks(rangeType: RangeType) {
  return {
    // In v3, callbacks receive a lifecycle object as the first argument, followed by the event parameters
    onEnterStartThenEnd(this: DurationFSM, _lifecycle: unknown, newEnd: moment.Moment) {
      setEndFixStart('', '', '', newEnd, rangeType);
    },
    onEnterStartThenDuration(this: DurationFSM, _lifecycle: unknown, newDuration: moment.Duration) {
      setDurationFixStart('', '', '', newDuration, rangeType);
    },
    onEnterEndThenStart(this: DurationFSM, _lifecycle: unknown, newStart: moment.Moment) {
      setStartFixEnd('', '', '', newStart, rangeType);
    },
    onEnterEndThenDuration(this: DurationFSM, _lifecycle: unknown, newDuration: moment.Duration) {
      setDurationFixEnd('', '', '', newDuration, rangeType);
    },
    onEnterDurationThenStart(this: DurationFSM, _lifecycle: unknown, newStart: moment.Moment) {
      setStartFixDuration('', '', '', newStart, rangeType);
    },
    onEnterDurationThenEnd(this: DurationFSM, _lifecycle: unknown, newEnd: moment.Moment) {
      setEndFixDuration('', '', '', newEnd, rangeType);
    },
  } as const;
}

/**
 * Create array of all FSM states and allowed transitions.
 *
 * @return {Object[]} FSM state transition
 */
export function generateEvents() {
  return [
    {
      name: 'reset',
      from: '*',
      to: 'none',
    },
    {
      name: 'setStart',
      from: ['none', 'startThenDuration', 'endThenDuration', 'durationThenStart'],
      to: 'durationThenStart',
    },
    {
      name: 'setStart',
      from: ['startThenEnd', 'endThenStart', 'durationThenEnd'],
      to: 'endThenStart',
    },
    {
      name: 'setEnd',
      from: ['none', 'startThenDuration', 'endThenDuration', 'durationThenEnd'],
      to: 'durationThenEnd',
    },
    {
      name: 'setEnd',
      from: ['startThenEnd', 'durationThenStart', 'endThenStart'],
      to: 'startThenEnd',
    },
    {
      name: 'setDuration',
      from: ['none', 'startThenEnd', 'endThenDuration', 'durationThenEnd'],
      to: 'endThenDuration',
    },
    {
      name: 'setDuration',
      from: ['endThenStart', 'durationThenStart', 'startThenDuration'],
      to: 'startThenDuration',
    },
  ];
}

/**
 * Resets both FSMs to their default state
 */
export function cleanup() {
  _.forEach(['displayRange', 'investigateRange'], function (rangeType: RangeType) {
    window.clearTimeout(FSM.timeouts[rangeType]);
    FSM[rangeType].reset.call(FSM[rangeType]);
  });

  window.clearInterval(autoUpdateHeartbeat);
}

/**
 * Sets the end time of a range, keeping the start time fixed.
 * This is a callback for the FSM.
 *
 * @param event - Name of event triggered
 * @param from - Name of state transitioning from
 * @param to - Name of state transitioning to
 * @param newEnd - New end date for the range
 * @param rangeType - Range type, 'displayRange' or 'investigateRange'
 */
export function setEndFixStart(
  _event: string,
  _from: string,
  _to: string,
  newEnd: moment.Moment,
  rangeType: RangeType,
) {
  const start = getRange(rangeType).start;
  const duration = getRange(rangeType).duration;
  if (newEnd.isBefore(start)) {
    exposedExports[rangeType].updateTimes(moment.utc(newEnd).subtract(duration), newEnd);
  } else {
    exposedExports[rangeType].updateTimes(start, newEnd);
  }

  startFSMTimeout(rangeType);
}

/**
 * Sets the duration length of a range, keeping the start time fixed.
 * This is a callback for the FSM.
 *
 * @param event - Name of event triggered
 * @param from - Name of state transitioning from
 * @param to - Name of state transitioning to
 * @param newDuration - New duration for the range
 * @param rangeType - Range type, 'displayRange' or 'investigateRange'
 */
export function setDurationFixStart(
  _event: string,
  _from: string,
  _to: string,
  newDuration: moment.Duration,
  rangeType: RangeType,
) {
  const start = getRange(rangeType).start;
  exposedExports[rangeType].updateTimes(start, moment.utc(start).add(newDuration));
  startFSMTimeout(rangeType);
}

/**
 * Sets the start time of a range, keeping the end time fixed.
 * This is a callback for the FSM.
 *
 * @param event - Name of event triggered
 * @param from - Name of state transitioning from
 * @param to - Name of state transitioning to
 * @param newStart - New start date for the range
 * @param rangeType - Range type, 'displayRange' or 'investigateRange'
 */
export function setStartFixEnd(
  _event: string,
  _from: string,
  _to: string,
  newStart: moment.Moment,
  rangeType: RangeType,
) {
  const end = getRange(rangeType).end;
  const duration = getRange(rangeType).duration;
  if (end.isBefore(newStart)) {
    exposedExports[rangeType].updateTimes(newStart, moment.utc(newStart).add(duration));
  } else {
    exposedExports[rangeType].updateTimes(newStart, end);
  }

  startFSMTimeout(rangeType);
}

/**
 * Sets the duration length of a range, keeping the end time fixed.
 * This is a callback for the FSM.
 *
 * @param event - Name of event triggered
 * @param from - Name of state transitioning from
 * @param to - Name of state transitioning to
 * @param newDuration - New duration length for the range
 * @param rangeType - Range type, 'displayRange' or 'investigateRange'
 */
export function setDurationFixEnd(
  _event: string,
  _from: string,
  _to: string,
  newDuration: moment.Duration,
  rangeType: RangeType,
) {
  const end = getRange(rangeType).end;
  exposedExports[rangeType].updateTimes(moment.utc(end).subtract(newDuration), end);
  startFSMTimeout(rangeType);
}

/**
 * Sets the start time of a range, keeping the duration length fixed.
 * This is a callback for the FSM.
 *
 * @param event - Name of event triggered
 * @param from - Name of state transitioning from
 * @param to - Name of state transitioning to
 * @param newStart - New start date for the range
 * @param rangeType - Range type, 'displayRange' or 'investigateRange'
 */
export function setStartFixDuration(
  _event: string,
  _from: string,
  _to: string,
  newStart: moment.Moment,
  rangeType: RangeType,
) {
  const duration = getRange(rangeType).duration;
  exposedExports[rangeType].updateTimes(newStart, moment.utc(newStart).add(duration));
  startFSMTimeout(rangeType);
}

/**
 * Sets the end time of a range, keeping the duration length fixed.
 * This is a callback for the FSM.
 *
 * @param event - Name of event triggered
 * @param from - Name of state transitioning from
 * @param to - Name of state transitioning to
 * @param newEnd - New end date for the range
 * @param rangeType - Range type, 'displayRange' or 'investigateRange'
 */
export function setEndFixDuration(
  _event: string,
  _from: string,
  _to: string,
  newEnd: moment.Moment,
  rangeType: RangeType,
) {
  const duration = getRange(rangeType).duration;
  exposedExports[rangeType].updateTimes(moment.utc(newEnd).subtract(duration), newEnd, isAutoUpdate);
  startFSMTimeout(rangeType);
}

/**
 * Resets to the FSM to initial conditions.
 *
 * @param rangeType - Range type, 'displayRange' or 'investigateRange'
 */
export function startFSMTimeout(rangeType: RangeType) {
  window.clearTimeout(FSM.timeouts[rangeType]);
  FSM.timeouts[rangeType] = window.setTimeout(FSM[rangeType].reset.bind(FSM[rangeType]), FSM.timeouts.timerLength);
}

export function triggerUpdateDuration(duration: moment.Duration, rangeType: RangeType) {
  const fsm = FSM[rangeType];
  const previousState = fsm.state;
  fsm.setDuration.call(fsm, duration);

  // If no transition occurred, then we may be in the same state as previously; if so, manually trigger
  if (fsm.state === previousState && previousState !== 'none') {
    switch (previousState) {
      case 'endThenDuration':
        setDurationFixEnd('', '', '', duration, rangeType);
        break;
      case 'startThenDuration':
        setDurationFixStart('', '', '', duration, rangeType);
        break;
    }
    startFSMTimeout(rangeType);
  }
}

export function triggerUpdateStart(start: moment.Moment, rangeType: RangeType) {
  const fsm = FSM[rangeType];
  const previousState = fsm.state;
  fsm.setStart.call(fsm, start);
  // If no transition occurred, then we may be in the same state as previously; if so, manually trigger
  if (fsm.state === previousState && previousState !== 'none') {
    switch (previousState) {
      case 'endThenStart':
        setStartFixEnd('', '', '', start, rangeType);
        break;
      case 'durationThenStart':
        setStartFixDuration('', '', '', start, rangeType);
        break;
    }
    startFSMTimeout(rangeType);
  }
}

export function triggerUpdateEnd(end: moment.Moment, rangeType: RangeType) {
  const fsm = FSM[rangeType];
  const previousState = fsm.state;
  fsm.setEnd.call(fsm, end);

  // If no transition occurred, then we may be in the same state as previously; if so, manually trigger
  if (fsm.state === previousState && previousState !== 'none') {
    switch (previousState) {
      case 'startThenEnd':
        setEndFixStart('', '', '', end, rangeType);
        break;
      case 'durationThenEnd':
        setEndFixDuration('', '', '', end, rangeType);
        break;
    }
    startFSMTimeout(rangeType);
  }
}

/************************************************ Display Range ************************************************/

/**
 * Updates the display range start and end times. Duration is automatically adjusted to match the new range.
 * If the start time is after the end time, the values are rejected and no change is made.
 * @param start - New start time
 * @param end   - New end time
 * @param skipLockCheck - True to skip checking if the workbook is locked
 * @param skipInvestigateCheck - True to skip checking if the investigate range should be expanded
 */
export function displayRangeUpdateTimes(
  start: number | moment.Moment,
  end: number | moment.Moment,
  skipLockCheck = false,
  skipInvestigateCheck = false,
) {
  const startMoment = moment.utc(start);
  const endMoment = moment.utc(end);
  if (startMoment >= endMoment) {
    return;
  }

  dispatchUpdateDisplayRangeTimes(startMoment, endMoment, false, skipInvestigateCheck, skipLockCheck);
}

/**
 * Resets the display range times to a default. Default is a time range {@link DEFAULT_DISPLAY_RANGE_DURATION_DAYS}
 * days long ending at the current time.
 */
export function displayRangeResetTimes() {
  const endMoment = moment.utc();
  const startMoment = moment.utc(endMoment).subtract(DEFAULT_DISPLAY_RANGE_DURATION_DAYS, 'days');
  dispatchUpdateDisplayRangeTimes(startMoment, endMoment, false);
}

/**
 * Sets the display range start and end time by adding a multiple of the current duration to both the start and end.
 * The duration is unchanged.
 *
 * @param multiple - Multiple of the current duration to use as the offset, positive to step forward or
 *                          negative to step backward.
 */
export function displayRangeStepByDuration(multiple: number) {
  const currentDisplayRange = getDurationDisplayRange();
  const currentInvestigateRange = getDurationInvestigateRange();
  const stepInterval = currentDisplayRange.duration.asMilliseconds() * multiple;
  const endMoment = moment.utc(currentDisplayRange.end).add(stepInterval, 'ms');
  const startMoment = moment.utc(currentDisplayRange.start).add(stepInterval, 'ms');
  const oldInvestigateStartTime = currentInvestigateRange.start;
  const oldInvestigateEndTime = currentInvestigateRange.end;
  dispatchUpdateDisplayRangeTimes(startMoment, endMoment, false);

  if (endMoment > oldInvestigateEndTime) {
    investigateRange.updateTimes(
      moment.utc(endMoment.valueOf() - (oldInvestigateEndTime.valueOf() - oldInvestigateStartTime.valueOf())),
      endMoment,
    );
  } else if (startMoment < oldInvestigateStartTime) {
    investigateRange.updateTimes(
      startMoment,
      moment.utc(startMoment.valueOf() + (oldInvestigateEndTime.valueOf() - oldInvestigateStartTime.valueOf())),
    );
  }
}

/**
 * Sets the display range end time to 'now'. The display and investigate duration is unchanged.
 */
export function displayRangeStepToEnd() {
  const duration = getDurationDisplayRange().duration;
  const now = moment().utc().valueOf();
  sqDurationStore.actions.clearOffset();
  sqDurationStore.actions.setNow(now);

  if (sqTrendStore.isTrendViewCapsuleTime()) {
    flux.dispatch('TREND_SET_CAPSULE_TIME_OFFSETS', { lower: 0, upper: 0 }, PUSH_IGNORE);
  }

  dispatchUpdateDisplayRangeTimes(
    moment.utc(now + sqDurationStore.getState().autoUpdate.offset).subtract(duration),
    moment.utc(now + sqDurationStore.getState().autoUpdate.offset),
    true,
  );
}

async function restoreWorkstep(workbookId: string, worksheetIds: string[]) {
  const promises = worksheetIds.map(async (worksheetId) => {
    const workstep = await getWorkstep(workbookId, worksheetId);
    if (!workstep.previous) {
      return false;
    }
    return setCurrentWorkstep(workbookId, worksheetId, workstep.previous)
      .then((_success) => true)
      .catch((_error) => false);
  });

  return Promise.all(promises).then((values) => {
    if (values.every((value) => value)) {
      successToast({ messageKey: 'RANGE_SELECTOR.COPY_TO_ALL_WORKSHEETS_UNDO_SUCCESS' });
    } else {
      warnToast({ messageKey: 'RANGE_SELECTOR.COPY_TO_ALL_WORKSHEETS_UNDO_WARNING' });
    }
  });
}

/**
 * Copies the display range from the current worksheet to all other worksheets.
 */
export async function displayRangeCopyToAllWorksheets() {
  const workbookId = sqWorkbookStore.workbookId;
  const durationState = sqDurationStore.getState();
  const autoUpdate = durationState.autoUpdate;
  const displayRange = getDurationDisplayRange();
  const investigateRange = getDurationInvestigateRange();

  const promises: Promise<[string, boolean]>[] = sqWorkbookStore.worksheets
    .filter((worksheet) => worksheet.worksheetId !== sqWorkbenchStore.stateParams.worksheetId)
    .map(async (worksheet): Promise<[string, boolean]> => {
      const worksheetId = worksheet.worksheetId;
      if (!worksheetId) {
        return ['', false];
      }
      const workstep = await getWorkstep(workbookId, worksheetId);
      const durationStore = workstep.current.state.stores['sqDurationStore'];
      if (durationStore) {
        durationStore['autoUpdate'] = autoUpdate;
        durationStore['displayRange'] = displayRange;
        durationStore['investigateRange'] = investigateRange;
        try {
          await pushWorkstep(workbookId, worksheetId, workstep.current.state);
        } catch (e) {
          setCurrentWorkstep(workbookId, worksheetId, workstep.current.id);
          return [worksheetId, false];
        }
      }
      return [worksheetId, true];
    });

  return Promise.all(promises).then((values) => {
    const success = values.filter(([_, val]) => val).map(([id, _]) => id);
    const failed = values.filter(([_, val]) => !val);
    const buttonAction = () => restoreWorkstep(workbookId, success);

    if (_.isEmpty(failed)) {
      successToast({
        messageKey: 'RANGE_SELECTOR.COPY_TO_ALL_WORKSHEETS_SUCCESS',
        buttonLabelKey: 'UNDO',
        buttonAction,
      });
    } else {
      warnToast({
        messageKey: 'RANGE_SELECTOR.COPY_TO_ALL_WORKSHEETS_WARNING',
        buttonLabelKey: 'UNDO',
        buttonAction,
      });
    }
  });
}

/**
 * Sets the display range with the start/end times from the investigate range
 */
export function displayRangeCopyFromInvestigateRange() {
  displayRange.updateTimes(
    moment.utc(getDurationInvestigateRange().start),
    moment.utc(getDurationInvestigateRange().end),
  );
}

/**
 * Dispatch the display range update times change and re-request series data if they have changed
 * @param start - New start time as a Moment.
 * @param end - New end time as a Moment.
 * @param keepInvestigateDuration - True if the investigate Range duration should be held
 * @param [skipInvestigateCheck=false] - True to skip checking if the investigate range should be expanded
 * @param [skipLockCheck=false] - True to skip checking if the workbook is locked
 */ export function dispatchUpdateDisplayRangeTimes(
  start: moment.Moment,
  end: moment.Moment,
  keepInvestigateDuration?: boolean,
  skipInvestigateCheck = false,
  skipLockCheck = false,
) {
  const maintainInvestigateDuration = keepInvestigateDuration || isAutoUpdate;
  const currentDisplayRange = getDurationDisplayRange();
  if (!start.isSame(currentDisplayRange.start) || !end.isSame(currentDisplayRange.end)) {
    if (sqWorkbookStore.isLocked && !skipLockCheck) {
      setLockedChangeMade();
    }
    sqDurationStore.actions.updateDisplayRangeTimes({
      start: start.valueOf(),
      end: end.valueOf(),
      isAutoUpdate: Boolean(isAutoUpdate),
    });

    if (!skipInvestigateCheck) {
      if (maintainInvestigateDuration) {
        shiftInvestigateRangeIfRequired();
      } else {
        expandInvestigateRangeIfRequired();
      }
    }

    // If not an auto update, then trigger computation of the auto update offset
    if (!isAutoUpdate) {
      autoUpdateComputeOffset();
    }

    // We defer calling _.debounce until the first time it is used so that tests have an opportunity to
    // replace _.debounce. See CRAB-7098
    if (!debouncedOnDurationChange) {
      debouncedOnDurationChange = _.debounce(onDurationChange, DEBOUNCE.MEDIUM);

      /**
       * Called when the display range changes so that data that depends on the display range is updated.
       */
      function onDurationChange() {
        // Reset capsule panel page to the first page before the table and chart capsules are fetched
        // The action is dispatched directly instead of calling the action method to avoid fetching capsules twice
        flux.dispatch('TREND_RESET_CAPSULE_PANEL_OFFSET', null, PUSH_IGNORE);
        flux.dispatch('TREND_RESET_CAPSULE_TIME_OFFSETS', null, PUSH_IGNORE);

        if (sqTrendStore.isTrendViewCapsuleTime()) {
          return fetchTableAndChartCapsules();
        } else {
          return fetchAllItems({
            skipProps: true,
            skipTimebar: true,
          });
        }
      }
    }

    if (!isAutoUpdateInit) {
      debouncedOnDurationChange();
    }
  }
}

/**
 * Overwrites the display range and auto updating in the store. Does not cause the trend to refresh data because
 * this method was made for overwriting the display range via url parameters in the middle of a rehydration with
 * the expectation that fetchAllItems would be called at a later time.
 *
 * @param start - New start time as iso string.
 * @param end - New end time as iso string.
 */
export function displayRangeSetParamsInStore(start: string, end: string) {
  const startMoment = parseISODate(start);
  const endMoment = parseISODate(end);
  if (startMoment.isValid() && endMoment.isValid() && startMoment.isBefore(endMoment)) {
    sqDurationStore.actions.updateDisplayRangeTimes({
      start: startMoment.valueOf(),
      end: endMoment.valueOf(),
      isAutoUpdate: false,
    });
  }
}

/********************************************** Investigate Range **********************************************/

/**
 * Updates the investigate range start and end times. Duration is automatically adjusted to match the new range.
 * If the start time is after the end time, the values are rejected and no change is made.
 * @param start - New start time
 * @param end   - New end time
 */
export function investigateRangeUpdateTimes(
  start: number | moment.Moment,
  end: number | moment.Moment,
  _skipLockCheck = false,
) {
  const startMoment = moment.utc(start);
  const endMoment = moment.utc(end);

  if (startMoment >= endMoment) {
    return;
  }

  exposedForTesting.dispatchUpdateInvestigateRangeTimes(startMoment, endMoment);
  if (!isAutoUpdate) {
    autoUpdateComputeOffset();
  }
}

/**
 * Sets the investigate range with the start/end times from the display range
 */
export function investigateRangeCopyFromDisplayRange() {
  investigateRange.updateTimes(moment.utc(getDurationDisplayRange().start), moment.utc(getDurationDisplayRange().end));
}

/**
 * Dispatch the investigate range update times change
 * @param start - New start time as a Moment.
 * @param end - New end time as a Moment.
 * @param [skipDisplayCheck=false] - True to skip checking if the display range should be shrunk
 */
function dispatchUpdateInvestigateRangeTimes(start: moment.Moment, end: moment.Moment, skipDisplayCheck = false) {
  const currentInvestigateRange = getDurationInvestigateRange();
  if (!start.isSame(currentInvestigateRange.start) || !end.isSame(currentInvestigateRange.end)) {
    sqDurationStore.actions.updateInvestigateRangeTimes({
      start: start.valueOf(),
      end: end.valueOf(),
      isAutoUpdate: Boolean(isAutoUpdate),
    });
    if (!skipDisplayCheck) {
      shrinkDisplayRangeIfRequired();
    }

    // We defer calling _.debounce until the first time it is used so that tests have an opportunity to
    // replace _.debounce. See CRAB-7098
    if (!debouncedOnInvestigateChange) {
      debouncedOnInvestigateChange = _.debounce(onInvestigateChange, DEBOUNCE.MEDIUM);

      /**
       * Called when the investigate range changes so that data that depends on the investigate range is updated.
       */
      function onInvestigateChange() {
        handleInvestigateRangeUpdate();
      }
    }

    debouncedOnInvestigateChange();
  }
}

/**
 * Shifts the investigation range so that it encompasses the display range.
 */
export function shiftInvestigateRangeIfRequired() {
  const displayRange = getDurationDisplayRange();
  const investigateRange = getDurationInvestigateRange();
  const startDelta = investigateRange.start.valueOf() - displayRange.start.valueOf();
  const endDelta = investigateRange.end.valueOf() - displayRange.end.valueOf();

  if (investigateRange.duration < displayRange.duration) {
    // If the investigate range is smaller than the display range, shift so that the ends are the same.
    exposedForTesting.dispatchUpdateInvestigateRangeTimes(
      moment.utc(investigateRange.start.valueOf() - endDelta),
      moment.utc(investigateRange.end.valueOf() - endDelta),
      true,
    );
  } else if (startDelta > 0 && endDelta > 0) {
    // If both deltas are positive, then we shift the investigate range to the left to align the starts.
    exposedForTesting.dispatchUpdateInvestigateRangeTimes(
      moment.utc(investigateRange.start.valueOf() - startDelta),
      moment.utc(investigateRange.end.valueOf() - startDelta),
      true,
    );
  } else if (startDelta < 0 && endDelta < 0) {
    // If both deltas are negative, then we shift the investigate range to the right to align the ends.
    // This can't actually happen right now, since we keep the investigate range end constant when processing
    // a new investigate duration.
    exposedForTesting.dispatchUpdateInvestigateRangeTimes(
      moment.utc(investigateRange.start.valueOf() - endDelta),
      moment.utc(investigateRange.end.valueOf() - endDelta),
      true,
    );
  }
}

/**
 * If required, expands the investigate range to encompass the display range
 */
export function expandInvestigateRangeIfRequired() {
  const displayRange = getDurationDisplayRange();
  const investigateRange = getDurationInvestigateRange();
  const newStart =
    displayRange.start < investigateRange.start || displayRange.duration > investigateRange.duration
      ? displayRange.start
      : investigateRange.start;
  const newEnd =
    displayRange.end > investigateRange.end || displayRange.duration > investigateRange.duration
      ? displayRange.end
      : investigateRange.end;
  exposedForTesting.dispatchUpdateInvestigateRangeTimes(newStart, newEnd, true);
}

/**
 * If required, shrinks the display range to remain within the investigate range
 */
export function shrinkDisplayRangeIfRequired() {
  const displayRange = getDurationDisplayRange();
  const investigateRange = getDurationInvestigateRange();
  if (displayRange.end > investigateRange.end) {
    const newEnd = investigateRange.end;
    const newStart = moment.utc(
      Math.max(newEnd.valueOf() - displayRange.duration.asMilliseconds(), investigateRange.start.valueOf()),
    );
    dispatchUpdateDisplayRangeTimes(newStart, newEnd, false, true);
  }

  if (displayRange.start < investigateRange.start) {
    const newStart = investigateRange.start;
    const newEnd = moment.utc(
      Math.min(newStart.valueOf() + displayRange.duration.asMilliseconds(), investigateRange.end.valueOf()),
    );
    dispatchUpdateDisplayRangeTimes(newStart, newEnd, false, true);
  }
}

export const exposedForTesting = {
  dispatchUpdateInvestigateRangeTimes,
};
